/**
 * Catálogo de CATEGORÍAS de datos para el "Reset selectivo" del PMIS.
 *
 * Agrupa las tablas Dexie en grupos entendibles para que el ADMIN_PMIS pueda
 * elegir QUÉ información resetear (en vez del "todo o nada" del reset total).
 *
 * Cada categoría declara:
 *   - `key`        identificador estable (se persiste en la selección/auditoría).
 *   - `label`      nombre visible en la lista de selección.
 *   - `descripcion` qué se borra exactamente (para que el admin sepa).
 *   - `tablas`     tablas Dexie que se limpian al elegir la categoría.
 *   - `entidades`  entities equivalentes en Supabase (para el wipe selectivo en
 *                  modo nube; vacío si esa categoría no se migró a Supabase).
 *   - `flujo`      true = "dato de flujo del proyecto" (se marca por defecto en
 *                  una limpieza típica); false = catálogo/usuarios/configuración
 *                  (se conserva salvo que el admin lo marque explícitamente).
 *
 * La unión de todas las categorías cubre las 28 tablas → seleccionar TODO
 * equivale a un reset total.
 */

import type { Table } from "dexie";
import { localDb } from "./db";

export interface CategoriaReset {
  key: string;
  label: string;
  descripcion: string;
  tablas: Table[];
  entidades: string[];
  flujo: boolean;
}

export const CATEGORIAS_RESET: CategoriaReset[] = [
  // ── Datos de flujo del proyecto ────────────────────────────────────────
  {
    key: "afes",
    label: "AFEs y presupuestos",
    descripcion: "Budgets y AFEs (alcances, costos planeados, distribución mensual).",
    tablas: [localDb.budgets, localDb.afes],
    entidades: ["budget", "afe"],
    flujo: true,
  },
  {
    key: "sos",
    label: "Órdenes de trabajo y tareas",
    descripcion: "OTs y sus tareas/cronograma.",
    tablas: [localDb.sos, localDb.tareas],
    entidades: ["so", "tarea"],
    flujo: true,
  },
  {
    key: "rds",
    label: "Reportes diarios (RD)",
    descripcion: "Reportes diarios de avance de obra.",
    tablas: [localDb.rds],
    entidades: ["rd"],
    flujo: true,
  },
  {
    key: "actas",
    label: "Actas",
    descripcion: "Actas de avance/cobro y sus líneas.",
    tablas: [localDb.actas],
    entidades: ["acta"],
    flujo: true,
  },
  {
    key: "compras",
    label: "Compras (PR, PO, MR)",
    descripcion: "Requisiciones (PR), órdenes de compra (PO) y requisiciones de material (MR).",
    tablas: [localDb.prs, localDb.pos, localDb.mrs],
    entidades: ["pr", "po", "mr"],
    flujo: true,
  },
  {
    key: "eventos",
    label: "Eventos / handovers",
    descripcion: "Eventos del flujo y traspasos (handovers) entre roles.",
    tablas: [localDb.eventos],
    entidades: ["evento"],
    flujo: true,
  },
  {
    key: "riesgos",
    label: "Riesgos",
    descripcion: "Registro de riesgos, catálogo de riesgos y snapshots de burndown.",
    tablas: [localDb.riesgos, localDb.catalogosRiesgos, localDb.riskBurndownSnapshots],
    entidades: ["riesgo"],
    flujo: true,
  },
  {
    key: "stakeholders",
    label: "Stakeholders",
    descripcion: "Interesados del proyecto (matriz poder/interés).",
    tablas: [localDb.stakeholders],
    entidades: ["stakeholder"],
    flujo: true,
  },
  {
    key: "comisionamiento",
    label: "Comisionamiento",
    descripcion: "Listados de pendientes, sistemas, subsistemas y pendientes de comisionamiento.",
    tablas: [
      localDb.listadosPendientes,
      localDb.pendientesComm,
      localDb.sistemasComm,
      localDb.subsistemasComm,
    ],
    entidades: [],
    flujo: true,
  },
  {
    key: "mejoras",
    label: "Mejoras / lecciones aprendidas",
    descripcion: "Reportes de mejora y lecciones aprendidas.",
    tablas: [localDb.mejoras],
    entidades: ["mejora"],
    flujo: true,
  },
  {
    key: "gastosSap",
    label: "Gastos SAP (AC real)",
    descripcion: "Registro de gastos causados importados de SAP (BD Mes).",
    tablas: [localDb.gastosSap],
    entidades: ["gastoSap"],
    flujo: true,
  },
  {
    key: "notificaciones",
    label: "Bitácora de notificaciones enviadas",
    descripcion: "Histórico de correos/notificaciones que el PMIS intentó enviar.",
    tablas: [localDb.notificacionesEnviadas],
    entidades: [],
    flujo: true,
  },
  {
    key: "auditoria",
    label: "Bitácora de auditoría",
    descripcion: "Registro de acciones administrativas (incluye los propios resets).",
    tablas: [localDb.auditLog],
    entidades: [],
    flujo: true,
  },

  // ── Catálogos, usuarios y configuración (se conservan por defecto) ──────
  {
    key: "usuarios",
    label: "Usuarios (personal)",
    descripcion: "Padrón de usuarios y roles. ⚠️ Si lo borra, deberá recrear los accesos.",
    tablas: [localDb.usuarios],
    entidades: ["usuario"],
    flujo: false,
  },
  {
    key: "costes",
    label: "Catálogo de clases de coste",
    descripcion: "Catálogo de clases de coste (A111001, etc.). Estructural.",
    tablas: [localDb.costesCatalogo],
    entidades: ["costeCatalogo"],
    flujo: false,
  },
  {
    key: "contratos",
    label: "Contratos marco y tarifas",
    descripcion: "Contratos marco y sus tarifas. Base para OTs y actas.",
    tablas: [localDb.contratosMarco, localDb.tarifas],
    entidades: ["contrato"],
    flujo: false,
  },
  {
    key: "configNotif",
    label: "Configuración de notificaciones y listas de distribución",
    descripcion: "Reglas de notificación y listas de distribución (a quién llega cada correo).",
    tablas: [localDb.configuracionesNotificacion, localDb.listasDistribucion],
    entidades: [],
    flujo: false,
  },
  {
    key: "configIA",
    label: "Configuración de IA",
    descripcion: "Parámetros del asistente de IA. (Las claves nunca se muestran).",
    tablas: [localDb.configuracionesIA],
    entidades: [],
    flujo: false,
  },
];

/** Todas las keys (selección completa = reset total). */
export const TODAS_LAS_CATEGORIAS = CATEGORIAS_RESET.map((c) => c.key);

/** Keys de "datos de flujo" — preselección típica de una limpieza. */
export const CATEGORIAS_FLUJO = CATEGORIAS_RESET.filter((c) => c.flujo).map(
  (c) => c.key,
);
